import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4">
    <div className="absolute inset-0 radial-violet pointer-events-none" />
    <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 z-10">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
        <span className="font-serif font-bold text-white text-sm">g</span>
      </div>
      <span className="font-serif font-bold text-lg text-white">grime.bio</span>
    </Link>
    <div className="relative w-full max-w-sm animate-fade-up">
      <div className="rounded-2xl border border-[#22222B] bg-[#111115] p-8 shadow-2xl">
        <h1 className="font-serif font-bold text-3xl text-white text-center">{title}</h1>
        <p className="text-sm text-zinc-500 text-center mt-2 mb-7">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <input
      {...props}
      className="w-full bg-[#18181F] border border-[#22222B] focus:border-violet-500/60 rounded-xl px-4 py-2.5 text-white text-sm outline-none transition-colors placeholder:text-zinc-600"
    />
  </div>
);

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.username}!`);
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to manage your bio page">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Username" data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your username" autoComplete="username" />
        <Field label="Password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white font-medium rounded-xl py-2.5 transition-colors shadow-[0_0_20px_rgba(139,92,246,0.3)]"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Log in
        </button>
      </form>
      <p className="text-center text-sm text-zinc-500 mt-6">
        No account?{" "}
        <Link to="/register" className="text-violet-400 hover:text-violet-300" data-testid="to-register-link">Register</Link>
      </p>
    </AuthShell>
  );
}
